/**
 * Contenido de los textos legales del 51 Escuadrón Virtual.
 *
 * Fuente única de verdad para los cuatro documentos legales (aviso
 * legal, política de privacidad, términos de uso y política de
 * cookies). Los consume el submenú ACERCA DE del terminal CRT, que los
 * formatea para pantalla con `crt-format.ts`.
 *
 * Los datos se modelan como estructura para que el formateador del CRT
 * controle el volcado y la numeración de secciones de forma uniforme.
 *
 * Aviso: estos textos NO son asesoramiento jurídico. Su redacción final
 * debe revisarla ACSA antes de publicarse.
 */

/** Un bloque de texto dentro de una sección: párrafo o lista de viñetas. */
export type LegalBlock =
  | { readonly type: 'p'; readonly text: string }
  | { readonly type: 'list'; readonly items: readonly string[] };

/** Sección de un documento legal. El número lo añade el render. */
export interface LegalSection {
  /** Título de la sección, sin numerar. */
  readonly title: string;
  /** Contenido de la sección. */
  readonly body: readonly LegalBlock[];
}

/** Identificadores estables de los cuatro documentos legales. */
export type LegalDocumentId =
  | 'aviso-legal'
  | 'privacidad'
  | 'terminos'
  | 'cookies';

/** Documento legal completo. */
export interface LegalDocument {
  /** Identificador estable del documento. */
  readonly id: LegalDocumentId;
  /** Título visible del documento. */
  readonly title: string;
  /** Bloques introductorios, antes de la primera sección numerada. */
  readonly intro: readonly LegalBlock[];
  /** Secciones numeradas del documento. */
  readonly sections: readonly LegalSection[];
}

/**
 * Fecha de última actualización de los textos legales, mostrada en cada
 * documento. Formato `DD-MM-AAAA`; actualizar al modificar el contenido
 * legal.
 */
export const LEGAL_LAST_UPDATED = '18-05-2026';

/** Email de contacto único para todos los asuntos legales y de datos. */
export const LEGAL_CONTACT_EMAIL = 'acsa@simulacion-aerea.es';

/** Helper interno: párrafo. */
const p = (text: string): LegalBlock => ({ type: 'p', text });

/** Helper interno: lista de viñetas. */
const ul = (...items: string[]): LegalBlock => ({ type: 'list', items });

/* ─── 1. Aviso legal ──────────────────────────────────────────────────── */

const AVISO_LEGAL: LegalDocument = {
  id: 'aviso-legal',
  title: 'AVISO LEGAL',
  intro: [
    p(
      'En cumplimiento del artículo 10 de la Ley 34/2002, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se facilitan a continuación los datos identificativos del titular de este sitio web.',
    ),
  ],
  sections: [
    {
      title: 'TITULARIDAD DEL SITIO',
      body: [
        p('El titular de esta web (https://51sqn.simulacion-aerea.es) es:'),
        ul(
          'Denominación: Asociación Cultural de Simulación Aérea (ACSA).',
          'CIF: G-65625071.',
          `Email de contacto: ${LEGAL_CONTACT_EMAIL}.`,
        ),
        p(
          'Esta web es la herramienta operativa interna del 51 Escuadrón Virtual, una agrupación de pilotos virtuales de DCS World sin ánimo de lucro que opera bajo el paraguas de ACSA. El 51 Escuadrón Virtual no tiene personalidad jurídica propia e independiente de la asociación.',
        ),
      ],
    },
    {
      title: 'PROPIEDAD INTELECTUAL',
      body: [
        p(
          'El código fuente, el diseño visual, el arte pixel-art y los elementos gráficos originales de esta web son propiedad intelectual de Juan Carlos Núñez (autor).',
        ),
        p(
          'La marca «51 Escuadrón Virtual», el escudo del escuadrón y los símbolos identificativos del mismo pertenecen colectivamente al 51 Escuadrón Virtual y a sus pilotos.',
        ),
        p(
          'Queda prohibida la reproducción, distribución, comunicación pública o transformación, total o parcial, de los contenidos de esta web sin autorización expresa y por escrito de sus titulares.',
        ),
      ],
    },
    {
      title: 'CONTENIDO DE LOS USUARIOS',
      body: [
        p(
          'Los contenidos que los pilotos del escuadrón publiquen en esta web (debriefings personales, capturas de pantalla, comentarios, fichas de perfil) son propiedad intelectual de cada autor. Al publicarlos, ceden a ACSA, como titular de la web, una licencia no exclusiva y sin contraprestación económica para mostrarlos dentro de este sitio.',
        ),
        p(
          'Cada autor mantiene el derecho a solicitar la retirada de su propio contenido en cualquier momento.',
        ),
      ],
    },
    {
      title: 'EXENCIÓN DE RESPONSABILIDAD',
      body: [
        p(
          'Esta web se ofrece «tal cual», para uso interno del 51 Escuadrón Virtual, sin garantías expresas o implícitas. ACSA y los responsables del sitio no se hacen responsables de:',
        ),
        ul(
          'Interrupciones del servicio derivadas del mantenimiento o de actualizaciones.',
          'Daños indirectos derivados del uso de la web.',
          'Pérdidas de datos por fallos técnicos, sin perjuicio de que se realizan copias de seguridad periódicas.',
          'El contenido publicado por los pilotos en sus debriefings o fichas de perfil.',
        ),
      ],
    },
    {
      title: 'LEGISLACIÓN APLICABLE',
      body: [
        p(
          'Esta web se rige por la legislación española. Para cualquier controversia, las partes se someten a los juzgados y tribunales competentes del domicilio del titular.',
        ),
      ],
    },
    {
      title: 'CONTACTO',
      body: [
        p(
          `Para cualquier consulta relacionada con este aviso legal, incluidas las peticiones de retirada de contenido, puede escribir a: ${LEGAL_CONTACT_EMAIL}`,
        ),
      ],
    },
  ],
};

/* ─── 2. Política de privacidad ───────────────────────────────────────── */

const PRIVACIDAD: LegalDocument = {
  id: 'privacidad',
  title: 'POLÍTICA DE PRIVACIDAD',
  intro: [
    p(
      'Esta política describe cómo recopilamos, usamos y protegemos los datos personales de los pilotos registrados en 51sqn.simulacion-aerea.es, en cumplimiento del Reglamento General de Protección de Datos (RGPD) y de la Ley Orgánica 3/2018 de Protección de Datos Personales y Garantía de los Derechos Digitales (LOPDGDD).',
    ),
  ],
  sections: [
    {
      title: 'RESPONSABLE DEL TRATAMIENTO',
      body: [
        ul(
          'Responsable: Asociación Cultural de Simulación Aérea (ACSA).',
          'CIF: G-65625071.',
          `Email de contacto: ${LEGAL_CONTACT_EMAIL}.`,
        ),
        p(
          `ACSA trata estos datos en el marco del 51 Escuadrón Virtual, agrupación de pilotos virtuales afiliada a la asociación. Las solicitudes recibidas en ${LEGAL_CONTACT_EMAIL} se canalizan internamente al responsable del tratamiento dentro del plazo legal establecido.`,
        ),
      ],
    },
    {
      title: 'QUÉ DATOS RECOGEMOS',
      body: [
        p('Al registrarte como piloto:'),
        ul(
          'Callsign (alias identificativo, público dentro de la web).',
          'Email (privado; solo para verificación de la cuenta y recuperación de contraseña).',
          'Contraseña (protegida mediante hash argon2id, irreversible).',
          'Fecha de alta.',
        ),
        p('Datos que tú decidas añadir voluntariamente a tu perfil:'),
        ul(
          'Foto o avatar: bien una imagen del catálogo predefinido, bien una fotografía que subas voluntariamente desde tu equipo. Si subes una fotografía propia, ten en cuenta que puede constituir una imagen identificativa tuya.',
          'Lema personal.',
          'Aeronave principal, base y especialidad.',
          'Información adicional libre.',
        ),
        p('Datos generados por tu uso de la web:'),
        ul(
          'Estadísticas de vuelo extraídas de los reportes oficiales (archivos .acmi de TacView) que sube el oficial de operaciones.',
          'Debriefings personales que publiques.',
          'NOTAMs y briefings que redactes.',
          'Fecha y hora del último acceso.',
        ),
        p(
          'Sobre tu dirección IP: no la almacenamos en nuestra base de datos. Se procesa de forma transitoria —en memoria, sin guardarse— para limitar los intentos de acceso y prevenir abusos, y se comunica a Cloudflare durante la verificación anti-bot del proceso de registro. Los registros técnicos del servidor pueden contenerla de forma temporal.',
        ),
        p('No recogemos en ningún caso:'),
        ul(
          'Datos de geolocalización.',
          'Información biométrica.',
          'Datos de tu navegación fuera de esta web.',
          'Datos con fines de analítica o publicidad.',
        ),
      ],
    },
    {
      title: 'PARA QUÉ USAMOS TUS DATOS',
      body: [
        p('Tus datos se usan exclusivamente para:'),
        ul(
          'Autenticarte y mantener tu sesión activa.',
          'Mostrar tu ficha y tus estadísticas dentro del logbook.',
          'Permitirte publicar contenido (debriefings, NOTAMs, briefings, etc.).',
          'Enviarte emails relacionados con tu cuenta (verificación de email y recuperación de contraseña).',
          'Elaborar métricas internas del escuadrón (totales agregados, sin identificar a individuos).',
        ),
      ],
    },
    {
      title: 'LEGITIMACIÓN',
      body: [
        p('El tratamiento de tus datos se basa en:'),
        ul(
          'Tu consentimiento expreso, otorgado al registrarte (art. 6.1.a del RGPD).',
          'La ejecución del servicio que solicitas al unirte como piloto del escuadrón (art. 6.1.b del RGPD).',
        ),
      ],
    },
    {
      title: 'CONSERVACIÓN',
      body: [
        p('Tus datos se conservan mientras mantengas tu cuenta activa.'),
        p(
          'Si solicitas el borrado de tu cuenta, tus datos personales (email, contraseña y datos de perfil) se eliminan en un plazo máximo de 30 días.',
        ),
        p(
          'Si una cuenta permanece sin actividad durante 2 años completos, podremos contactar con su titular por email para preguntarle si desea conservarla. Si no recibimos respuesta en 30 días, la cuenta se marca como inactiva y los datos personales se eliminan en el plazo estándar de 30 días.',
        ),
        p(
          'Los contenidos públicos que hayas generado (debriefings públicos, reportes de las misiones en las que participaste) pueden conservarse de forma anonimizada por interés histórico del escuadrón, sustituyendo tu callsign por una etiqueta genérica («PILOTO RETIRADO»).',
        ),
      ],
    },
    {
      title: 'DESTINATARIOS',
      body: [
        p('Tus datos se almacenan y procesan en:'),
        ul(
          'Servidor VPS administrado por ACSA.',
          'Cloudflare R2 (almacenamiento de archivos: capturas de pantalla, archivos .acmi y PDF).',
          'Resend (proveedor de email transaccional).',
          'Cloudflare Turnstile (verificación anti-bot, únicamente durante el registro).',
        ),
        p(
          'Cloudflare y Resend son proveedores con sede en Estados Unidos. Las transferencias internacionales de datos que ello implica se amparan en las garantías previstas por el RGPD (cláusulas contractuales tipo y/o el marco de adecuación de datos UE — EE. UU.).',
        ),
        p('No compartimos tus datos con terceros con fines comerciales.'),
      ],
    },
    {
      title: 'TUS DERECHOS',
      body: [
        p('Como titular de tus datos, tienes derecho a:'),
        ul(
          'Acceder a los datos que tenemos sobre ti.',
          'Rectificar los datos incorrectos.',
          'Solicitar la supresión de tus datos.',
          'Limitar el tratamiento de tus datos.',
          'Solicitar la portabilidad de tus datos (exportación).',
          'Oponerte al tratamiento.',
          'Retirar tu consentimiento en cualquier momento.',
        ),
        p(
          `Para ejercer cualquiera de estos derechos, escribe a: ${LEGAL_CONTACT_EMAIL}`,
        ),
        p(
          'Si consideras que no hemos atendido tu solicitud adecuadamente, puedes presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es).',
        ),
      ],
    },
    {
      title: 'SEGURIDAD',
      body: [
        p(
          'Aplicamos medidas técnicas y organizativas razonables para proteger tus datos:',
        ),
        ul(
          'Contraseñas protegidas mediante hash argon2id (irreversible).',
          'Comunicaciones cifradas por HTTPS con certificado válido.',
          'Sesiones gestionadas con cookies httpOnly y SameSite.',
          'Copias de seguridad periódicas del servidor.',
          'Acceso restringido al servidor.',
        ),
      ],
    },
    {
      title: 'PRIVACIDAD DE MENORES',
      body: [
        p('Esta web está dirigida a pilotos virtuales mayores de 16 años.'),
        p(
          'No recopilamos intencionadamente datos personales de menores de esa edad. Si tenemos constancia de haber recogido datos de un menor sin la autorización pertinente, los eliminaremos de forma inmediata.',
        ),
        p(
          'Si eres padre, madre o tutor legal y crees que un menor a tu cargo se ha registrado en esta web, contacta con nosotros para que tomemos las medidas oportunas.',
        ),
      ],
    },
    {
      title: 'ENLACES A TERCEROS',
      body: [
        p(
          'Esta web puede contener enlaces a sitios externos (la web de ACSA, redes de aviación virtual, proveedores que usamos internamente). No somos responsables de las políticas de privacidad de esos terceros; te recomendamos revisarlas antes de proporcionarles datos personales.',
        ),
      ],
    },
    {
      title: 'CAMBIOS EN ESTA POLÍTICA',
      body: [
        p(
          'Podemos actualizar esta política para reflejar cambios en nuestras prácticas, en la legislación aplicable o en los servicios que ofrecemos.',
        ),
        p(
          'Si los cambios son significativos, te avisaremos por email con al menos 30 días de antelación para que decidas si sigues usando la web.',
        ),
        p(
          'Los cambios menores (correcciones de erratas, mejoras de redacción) se publicarán directamente, actualizando la fecha de «última actualización» indicada al inicio del documento.',
        ),
      ],
    },
  ],
};

/* ─── 3. Términos de uso ──────────────────────────────────────────────── */

const TERMINOS: LegalDocument = {
  id: 'terminos',
  title: 'TÉRMINOS DE USO',
  intro: [p('Al registrarte y usar esta web, aceptas estos términos.')],
  sections: [
    {
      title: 'NATURALEZA DEL SERVICIO',
      body: [
        p(
          'Esta web es una herramienta interna del 51 Escuadrón Virtual, agrupación de pilotos virtuales de DCS World sin ánimo de lucro afiliada a ACSA. Está pensada para los miembros del escuadrón y sus invitados.',
        ),
        p(
          'No es un servicio comercial. No hay garantía de disponibilidad ni soporte profesional. Está mantenida por aficionados.',
        ),
      ],
    },
    {
      title: 'REGISTRO Y CUENTA',
      body: [
        p(
          'Para usar las funcionalidades reservadas necesitas registrarte con un callsign y un email válido. Al hacerlo, confirmas que:',
        ),
        ul(
          'El email es tuyo y tienes acceso a él.',
          'Vas a respetar a los demás pilotos y al escuadrón.',
          'No vas a usar la cuenta para fines distintos al simulador.',
        ),
        p(
          'Eres responsable de la seguridad de tu contraseña. Si pierdes el acceso, usa la función de recuperación de contraseña.',
        ),
      ],
    },
    {
      title: 'NORMAS DE CONDUCTA',
      body: [
        p('Al usar esta web te comprometes a:'),
        ul(
          'Tratar a los demás pilotos con respeto.',
          'No publicar contenido ofensivo, ilegal o que infrinja derechos de terceros.',
          'No intentar acceder a cuentas ajenas.',
          'No automatizar el uso de la web (bots, scrapers).',
          'Respetar las decisiones del oficial de operaciones y de los administradores.',
        ),
      ],
    },
    {
      title: 'CONTENIDO QUE PUBLICAS',
      body: [
        p(
          'Cuando publicas debriefings, capturas de pantalla, NOTAMs o cualquier otro contenido:',
        ),
        ul(
          'Mantienes la propiedad sobre lo que escribes y publicas.',
          'Concedes al escuadrón una licencia para mostrarlo en la web.',
          'Garantizas que el contenido es tuyo o que tienes derechos sobre él (en especial las capturas de DCS).',
          'Te comprometes a que el contenido respeta a los demás pilotos y al escuadrón.',
        ),
        p(
          'Los administradores pueden retirar contenido inadecuado sin necesidad de aviso previo.',
        ),
      ],
    },
    {
      title: 'SUSPENSIÓN O CIERRE DE CUENTA',
      body: [
        p('Los administradores pueden suspender o cerrar cuentas que:'),
        ul(
          'Incumplan estos términos.',
          'Generen problemas para otros pilotos o para el escuadrón.',
          'Hayan permanecido inactivas durante 2 años (ver el apartado «Conservación» de la Política de Privacidad).',
        ),
        p(
          'Tienes derecho a cerrar tu cuenta en cualquier momento contactando con un administrador.',
        ),
      ],
    },
    {
      title: 'CAMBIOS EN LA WEB Y EN LOS TÉRMINOS',
      body: [
        p(
          'El equipo se reserva el derecho de modificar funcionalidades, estructura o disponibilidad de la web sin previo aviso.',
        ),
        p(
          'Si cambiamos estos términos de forma significativa, te avisaremos por email para que decidas si sigues usando la web.',
        ),
      ],
    },
    {
      title: 'CONTACTO',
      body: [
        p(`Para cualquier duda sobre estos términos: ${LEGAL_CONTACT_EMAIL}`),
      ],
    },
  ],
};

/* ─── 4. Política de cookies ──────────────────────────────────────────── */

const COOKIES: LegalDocument = {
  id: 'cookies',
  title: 'POLÍTICA DE COOKIES',
  intro: [
    p('Esta web usa el mínimo número de cookies necesario para funcionar.'),
  ],
  sections: [
    {
      title: '¿QUÉ COOKIES USAMOS?',
      body: [
        p('Únicamente cookies técnicas necesarias para que la web funcione:'),
        ul(
          'Nombre: 51esc_session.',
          'Propósito: mantener tu sesión iniciada.',
          'Tipo: técnica (necesaria).',
          'Duración: 30 días, o hasta que cierres sesión.',
          'Atributos: httpOnly, Secure y SameSite=Lax.',
        ),
      ],
    },
    {
      title: '¿QUÉ NO USAMOS?',
      body: [
        ul(
          'NO usamos cookies de analítica (Google Analytics u otras).',
          'NO usamos cookies de publicidad.',
          'NO usamos cookies de redes sociales.',
          'NO usamos cookies de terceros con fines comerciales.',
          'NO hacemos seguimiento entre webs (tracking).',
        ),
      ],
    },
    {
      title: 'ALMACENAMIENTO LOCAL',
      body: [
        p(
          'Además de la cookie de sesión, esta web usa el almacenamiento local de tu navegador (que, técnicamente, no son cookies) para recordar tus preferencias y tu trabajo en curso:',
        ),
        ul(
          'En localStorage: las preferencias del CRT (perfil de monitor y sonido) y la preferencia de modo día/tarde/noche de la sala.',
          'En localStorage: el estado de la radio del escuadrón (playlists y posición de los controles).',
          'En IndexedDB: los archivos de audio que tú añadas a la radio del escuadrón.',
        ),
        p(
          'Todo este almacenamiento permanece únicamente en tu navegador y no se envía al servidor.',
        ),
      ],
    },
    {
      title: 'CONSENTIMIENTO',
      body: [
        p(
          'Como solo usamos cookies técnicas estrictamente necesarias, no se requiere un banner de consentimiento, conforme a las directrices de la Agencia Española de Protección de Datos.',
        ),
        p(
          'Si quieres eliminar tus cookies y tus datos locales, puedes hacerlo en cualquier momento desde las opciones de tu navegador.',
        ),
      ],
    },
    {
      title: 'CONTACTO',
      body: [
        p(
          `Para cualquier duda sobre esta política de cookies: ${LEGAL_CONTACT_EMAIL}`,
        ),
      ],
    },
  ],
};

/**
 * Los cuatro documentos legales, en orden de presentación. El submenú
 * ACERCA DE del CRT los lista en este orden.
 */
export const LEGAL_DOCUMENTS: readonly LegalDocument[] = [
  AVISO_LEGAL,
  PRIVACIDAD,
  TERMINOS,
  COOKIES,
];

/**
 * Devuelve el documento legal con el `id` dado. Lanza si no existe —
 * los cuatro ids son un tipo cerrado, así que en uso normal nunca falla.
 */
export function getLegalDocument(id: LegalDocumentId): LegalDocument {
  const doc = LEGAL_DOCUMENTS.find((d) => d.id === id);
  if (!doc) {
    throw new Error(`[legal] documento desconocido: ${id}`);
  }
  return doc;
}
